import { z } from 'zod';
/**
 * Campañas de correo.
 *
 * ☠️ Cada destinatario es un envío independiente, no una sola llamada con la
 * lista en copia oculta: es lo que permite saber QUIÉN abrió (un envío tiene un
 * único píxel de seguimiento) y darle a cada uno su enlace de baja. De propina,
 * el correo sale de verdad poco a poco, que es como tiene que salir por un
 * Gmail normal.
 */
export const MARKETING_CAMPAIGN_STATUSES = [
    'BORRADOR',
    'PROGRAMADA',
    'ENVIANDO',
    'PAUSADA',
    'ENVIADA',
    'CANCELADA',
];
export const MARKETING_RECIPIENT_STATUSES = [
    'PENDIENTE',
    'ENVIADO',
    'FALLIDO',
    'SUPRIMIDO',
    'CANCELADO',
];
/**
 * ☠️ TOPES DE GOOGLE, que es por donde sale esto.
 *
 * Workspace permite 2.000 mensajes al día POR USUARIO, y ese cupo se comparte
 * con el correo que esa persona escribe a mano y con los presupuestos que salen
 * por el mismo SMTP. Pasarse cierra la cuenta 24 horas. Por encima de 5.000 al
 * día el dominio queda marcado como remitente masivo de forma permanente.
 *
 * Por eso el tope por campaña se queda MUY por debajo: esto es «poquito a
 * poco», no una plataforma de envío masivo.
 */
export const MARKETING_DAILY_CAP_MAX = 400;
export const MARKETING_DAILY_CAP_DEFAULT = 150;
/** Segundos entre correo y correo. Un goteo, no una ráfaga. */
export const MARKETING_THROTTLE_MIN = 5;
export const MARKETING_THROTTLE_DEFAULT = 20;
export const MarketingCampaignStatsSchema = z.object({
    total: z.number().int(),
    pendientes: z.number().int(),
    enviados: z.number().int(),
    fallidos: z.number().int(),
    suprimidos: z.number().int(),
    cancelados: z.number().int(),
    /** Cuántos de los enviados se han abierto al menos una vez. */
    abiertos: z.number().int(),
    /**
     * Personas con al menos un clic que parece de una persona. Los antivirus de
     * correo abren todos los enlaces al entrar el mensaje, así que los clics
     * inmediatos no se cuentan — ver SEGUNDOS_CLIC_DE_MAQUINA en el servidor.
     */
    clicados: z.number().int(),
});
export const MarketingCampaignDtoSchema = z.object({
    id: z.string(),
    name: z.string(),
    subject: z.string(),
    bodyHtml: z.string(),
    bodyText: z.string().nullable(),
    status: z.enum(MARKETING_CAMPAIGN_STATUSES),
    senderAccountId: z.string().nullable(),
    /** La dirección por la que sale o salió, para poder enseñarla sin más viajes. */
    fromEmail: z.string().nullable(),
    tagIds: z.array(z.string()),
    dailyCap: z.number().int(),
    throttleSeconds: z.number().int(),
    /**
     * ☠️ Que se pueda apagar el seguimiento de clics NO es un capricho de
     * configuración: es la prueba material de que no es imprescindible para
     * prestar el servicio de envío. Con esto en false el correo sale exactamente
     * igual que si nada de esto existiera.
     */
    trackClicks: z.boolean(),
    scheduledFor: z.string().nullable(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    stats: MarketingCampaignStatsSchema,
});
export const MarketingCampaignListResponseSchema = z.object({
    items: z.array(MarketingCampaignDtoSchema),
});
export const MarketingRecipientDtoSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string().nullable(),
    clientId: z.string().nullable(),
    status: z.enum(MARKETING_RECIPIENT_STATUSES),
    sentAt: z.string().nullable(),
    errorMessage: z.string().nullable(),
    /** Cuántas veces se ha abierto su correo. 0 no significa «no lo ha leído». */
    opens: z.number().int(),
    firstOpenAt: z.string().nullable(),
    /** Clics crudos, incluidos los de los rastreadores automáticos. */
    clicks: z.number().int(),
    firstClickAt: z.string().nullable(),
});
export const MarketingCampaignDetailResponseSchema = z.object({
    campaign: MarketingCampaignDtoSchema,
    /** Enlaces del cuerpo que SÍ se miden. */
    enlacesMedidos: z.number().int(),
    /**
     * Y los que no. Se dice en pantalla en vez de presumir de un porcentaje de
     * clics calculado sobre menos enlaces de los que lleva el correo.
     */
    enlacesSinMedir: z.number().int(),
    /**
     * ☠️ Cuántos correos de marketing han salido HOY, de TODAS las campañas.
     *
     * El tope diario es por campaña, pero el de Google es por cuenta: dos
     * campañas de 150 pueden estar cada una dentro de su tope y sumar 300 sobre
     * un cupo de 2.000 que además comparte con el correo del día a día. Esto NO
     * bloquea nada —a este volumen sería un candado inútil— pero se dice en la
     * pantalla de lanzar, que es cuando importa.
     */
    enviadosHoyEnTotal: z.number().int(),
    recipients: z.array(MarketingRecipientDtoSchema),
});
export const CreateMarketingCampaignSchema = z.object({
    name: z.string().trim().min(1, 'La campaña necesita un nombre').max(120),
    subject: z.string().trim().min(1, 'Hace falta un asunto').max(200),
    bodyHtml: z.string().min(1).max(200000),
    bodyText: z.string().max(200000).nullable().optional(),
    senderAccountId: z.string().min(1).nullable().optional(),
    dailyCap: z.number().int().min(1).max(MARKETING_DAILY_CAP_MAX).default(MARKETING_DAILY_CAP_DEFAULT),
    throttleSeconds: z
        .number()
        .int()
        .min(MARKETING_THROTTLE_MIN)
        .max(3600)
        .default(MARKETING_THROTTLE_DEFAULT),
    trackClicks: z.boolean().default(true),
});
export const UpdateMarketingCampaignSchema = CreateMarketingCampaignSchema.partial();
/**
 * Construye la lista de destinatarios a partir de etiquetas de cliente y de
 * direcciones sueltas. Es un REEMPLAZO del conjunto pendiente, no un añadir:
 * la pantalla trabaja sobre la lista entera.
 */
export const SetMarketingAudienceSchema = z.object({
    tagIds: z.array(z.string().min(1)).max(50).default([]),
    /** Direcciones añadidas a mano, además de las que traigan las etiquetas. */
    extraEmails: z.string().trim().max(20000).default(''),
});
export const SetMarketingAudienceResponseSchema = z.object({
    agregados: z.number().int(),
    /** Quitados de la lista porque ya no están en el segmento. */
    quitados: z.number().int(),
    /** Los que no entran porque están dados de baja. Se dicen, no se esconden. */
    suprimidos: z.array(z.string()),
    /** Clientes de las etiquetas que NO tienen correo: no se pueden incluir. */
    sinCorreo: z.array(z.string()),
    invalidos: z.array(z.string()),
    total: z.number().int(),
});
/**
 * ☠️ Arrancar una campaña es mandar correo de verdad a gente de verdad. Se pide
 * confirmar el número exacto de destinatarios: es la última pantalla en la que
 * un error de segmentación todavía no ha salido por ahí.
 */
export const StartMarketingCampaignSchema = z.object({
    /** Cuántos destinatarios cree la pantalla que hay. Si no cuadra, no se envía. */
    expectedRecipients: z.number().int().min(1),
    /** ISO. Sin fecha, empieza ya. */
    scheduledFor: z.string().datetime().nullable().optional(),
});
/**
 * Cuentas del Buzón por las que puede salir una campaña.
 *
 * Es una lista de TODAS las cuentas de la empresa que saben enviar, no solo las
 * de quien mira: la decisión de Jesús es que el remitente sea configurable y
 * que normalmente salga por jorge@jr2.es, así que él tiene que poder elegir una
 * cuenta que no es la suya. Son direcciones de trabajo, ya visibles en el CRM.
 */
export const MarketingSenderDtoSchema = z.object({
    id: z.string(),
    emailAddress: z.string(),
    displayName: z.string().nullable(),
    ownerName: z.string(),
    /** La cuenta está en estado de error: se puede elegir, pero se avisa. */
    conProblemas: z.boolean(),
    /**
     * ☠️ Si esta cuenta NO sincroniza su bandeja de entrada, los rebotes de sus
     * campañas no los lee nadie: las direcciones muertas se quedan en la lista
     * gastando cupo del Gmail y reputación del dominio, en silencio. Se puede
     * enviar igual, pero hay que decirlo.
     */
    sincronizaRebotes: z.boolean(),
});
export const MarketingSenderListResponseSchema = z.object({
    items: z.array(MarketingSenderDtoSchema),
    /** Si no hay ninguna, la campaña saldrá por el SMTP del bufete (Ajustes). */
    firmFromEmail: z.string().nullable(),
});
/**
 * Redactar el cuerpo de una campaña con IA.
 *
 * ☠️ NO se reutiliza `POST /ai/draft` aunque exista y esté desplegado: aquel
 * devuelve MARKDOWN, y el cuerpo de una campaña es TEXTO PLANO que el sistema
 * convierte a HTML. Los asteriscos y las almohadillas del markdown le llegarían
 * literales al cliente. Lo que se reutiliza es el motor (`chatOnce`), que es
 * donde está el trabajo.
 */
export const DraftMarketingCampaignSchema = z.object({
    /** Qué quieres contar, en tus palabras. */
    instructions: z.string().trim().min(10, 'Cuéntame de qué va el correo').max(2000),
});
export const DraftMarketingCampaignResponseSchema = z.object({
    /** Propuesta de asunto. Quien redacta decide si la usa. */
    subject: z.string(),
    /** El cuerpo en texto plano, con las variables ya puestas donde tocan. */
    bodyText: z.string(),
});
//# sourceMappingURL=marketing-campaigns.js.map